import React, { useState } from 'react';
import RequestRepair from '../components/RequestRepair/RequestRepair';
import OrderReceived from '../components/OrderReceived/OrderReceived';
import FloatingButton from '../components/FloatingButton/FloatingButton';

export default function WarrantyPolicyPage() {
  const [showRepair, setShowRepair] = useState(false);
  const [showOrderReceived, setShowOrderReceived] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const handleRepairSubmit = (submittedOrderId) => {
    setOrderId(submittedOrderId);
    setShowRepair(false);
    setShowOrderReceived(true);
  };

  return (
    <>
      <div className="policy-page-wrapper">
        {/* Background Ambient Elements */}
        <div className="policy-bg-glow" aria-hidden="true" />
        <div className="policy-bg-dots-left" aria-hidden="true" />
        <div className="policy-bg-dots-right" aria-hidden="true" />

        <div className="policy-container">
          <header className="policy-header">
            <div className="policy-badge">
              <span className="star-icon">★</span>
              <span>WARRANTY COVERAGE</span>
            </div>

            <h1>
              Warranty <span className="gold-text">Policy</span>
            </h1>

            {/* Gold Diamond Divider */}
            <div className="heading-divider" aria-hidden="true">
              <div className="divider-line" />
              <div className="divider-diamond" />
              <div className="divider-line" />
            </div>

            <p className="subnote">Independent Repair Service • Not affiliated with any brand</p>
            <p className="effective-date">Effective Date: 07/08/2026</p>
          </header>

          <div className="policy-content">
            <p className="intro">
              At DoorStep Maintenance &amp; Repair (DSMR), we take pride in our workmanship and quality components. To provide you complete confidence, we offer limited warranty coverage on eligible repairs and replacement parts. This Warranty Policy sets forth the terms, coverage periods, claim procedures, and exclusions governing our repair warranties.
            </p>

            <section className="policy-section">
              <h2>1. Warranty Coverage &amp; Durations</h2>
              <ol>
                <li>Eligible repairs and replacement parts come with a limited warranty ranging from Testing Warranty up to 12 months, as specified on your invoice at the time of repair.</li>
                <li>Local or budget replacement parts, as well as specific minor component repairs, may carry a Testing Warranty (functional check at doorstep) or a shorter 30-day/90-day coverage window. Coverage terms are communicated clearly prior to repair.</li>
                <li>Warranty coverage strictly applies to the exact part replaced and specific issue serviced during the original repair job. It does not cover unrelated issues or pre-existing motherboard faults.</li>
              </ol>
            </section>

            <section className="policy-section">
              <h2>2. Warranty Claim Terms &amp; Fee Structure</h2>
              <ol>
                <li><strong>1st Claim Free:</strong> The first warranty claim for a qualifying repaired part/issue within the valid warranty period is handled 100% free of charge.</li>
                <li><strong>2nd &amp; Subsequent Claims:</strong> For any second or subsequent warranty claim on the same repair, a ₹599 technician inspection/service fee applies to cover travel, testing, and handling costs.</li>
                <li><strong>Service Delivery SLA:</strong>
                  <ul>
                    <li>Doorstep warranty claims are scheduled within <strong>up to 7 business days</strong> depending on technician route availability in your area.</li>
                    <li>For immediate or same-day warranty verification, customers may bring the device directly to our physical service center/store.</li>
                  </ul>
                </li>
              </ol>
            </section>

            <section className="policy-section">
              <h2>3. Eligibility Requirements</h2>
              <p>To claim warranty coverage, customers must fulfill the following criteria:</p>
              <ol>
                <li>The repair must have been performed directly by DSMR authorized personnel.</li>
                <li>The customer must present the official digital or physical invoice/bill issued by DSMR Solutions at the time of warranty claim. Official bills are generated solely by DSMR Solutions and delivered via WhatsApp, SMS, or Email within 24 hours post-payment. Doorstep technicians are not authorized to issue bills.</li>
                <li>Customers must contact DSMR Solutions directly for any billing inquiries; bills cannot be requested from technicians.</li>
                <li>It is the customer's responsibility to check and verify that all bill details (device model, serial/IMEI, part replaced, and warranty duration) are accurate upon receipt from DSMR Solutions.</li>
                <li>The warranty period is calculated continuously from the original repair completion date and is not extended or reset upon a warranty replacement.</li>
              </ol>
            </section>

            <section className="policy-section">
              <h2>4. Explicit Warranty Exclusions</h2>
              <p>The warranty shall be rendered null and void under any of the following circumstances:</p>
              <ol>
                <li><strong>Physical &amp; Display Damage:</strong> Any physical drops, cracks, broken glass, internal LCD/OLED bleeding, or green/white lines appearing on display panels after repair.</li>
                <li><strong>Liquid &amp; Moisture Exposure:</strong> Any water, moisture, sweat, or liquid damage occurring post-repair.</li>
                <li><strong>Third-Party Tampering:</strong> Opening, modifying, or servicing the device by another technician, unauthorized center, or the customer after our service.</li>
                <li><strong>Software &amp; OS Modifications:</strong> Operating system crashes, malware, unverified third-party app installations, or custom ROM flashing unless software repair was explicitly warranted.</li>
                <li><strong>Data &amp; Media Loss:</strong> Loss of personal files, media, or data during or after warranty service. Backup remains the customer's sole responsibility.</li>
                <li><strong>Battery Swelling &amp; Consumables:</strong> Battery performance degradation or swelling caused by overcharging, non-certified chargers, or high-temperature storage.</li>
                <li><strong>Declined Diagnostic Advice:</strong> Issues resulting from customer refusal of recommended secondary part replacements (e.g., electing a temporary fix over full assembly replacement).</li>
              </ol>
            </section>

            <section className="policy-section">
              <h2>5. Limitation of Liability</h2>
              <ol>
                <li>DSMR's sole liability under this warranty is restricted to repairing or replacing the defective part or re-performing the defective service originally provided.</li>
                <li>DSMR is not liable for indirect, incidental, special, or consequential damages, including data loss, lost revenues, device downtime, or secondary component degradation.</li>
              </ol>
            </section>

            <section className="policy-section">
              <h2>6. Warranty Claim Procedure</h2>
              <ol>
                <li>Submit a claim by contacting DSMR via phone (+91.8700319769), WhatsApp, or email with your invoice number and problem description.</li>
                <li>Our technical team will assess the issue remotely and schedule a doorstep inspection or store visit.</li>
                <li>Upon inspection, if the technician confirms the failure is due to a manufacturing part defect or installation error, repair/replacement will be executed under warranty terms.</li>
                <li>If inspection reveals physical damage, liquid ingress, or void criteria, standard out-of-warranty repair costs will be quoted.</li>
              </ol>
            </section>

            <section className="policy-section">
              <h2>7. Final Decision</h2>
              <p>DSMR reserves the right of final determination regarding warranty claim validity following technical diagnosis. Our technical evaluation of hardware condition shall be binding.</p>
            </section>

            <section className="policy-section">
              <h2>8. Contact for Warranty Support</h2>
              <div className="contact-info">
                <p><strong>DSMR Solutions — Warranty Desk</strong></p>
                <p>Website: dsmrsolutions.in</p>
                <p>Phone: +91.8700319769</p>
                <p>Email: support@dsmrsolutions.in</p>
              </div>
            </section>
          </div>
        </div>

        {showRepair && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <RequestRepair
              isModal={true}
              onClose={() => setShowRepair(false)}
              onSubmit={handleRepairSubmit}
            />
          </div>
        )}

        {showOrderReceived && (
          <OrderReceived
            orderId={orderId}
            onClose={() => setShowOrderReceived(false)}
          />
        )}



        <style>{`
          .policy-page-wrapper {
            min-height: 100vh;
            background: linear-gradient(135deg, #f9f4ee 0%, #fbf6f1 35%, #fdf8f5 70%, #fdf9f4 100%);
            padding: 8rem 1.5rem 4rem 1.5rem;
            position: relative;
            overflow: hidden;
            font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }

          .policy-bg-glow {
            position: absolute;
            top: -20px;
            left: 50%;
            transform: translateX(-50%);
            width: 900px;
            height: 480px;
            background: radial-gradient(ellipse at center, rgba(235, 205, 155, 0.35) 0%, rgba(250, 246, 240, 0) 70%);
            pointer-events: none;
            z-index: 0;
          }

          .policy-bg-dots-left {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 300px;
            background-image: radial-gradient(#C99E54 1.4px, transparent 1.4px);
            background-size: 22px 22px;
            opacity: 0.25;
            mask-image: linear-gradient(to right, rgba(0,0,0,0.8), transparent);
            -webkit-mask-image: linear-gradient(to right, rgba(0,0,0,0.8), transparent);
            pointer-events: none;
          }

          .policy-bg-dots-right {
            position: absolute;
            top: 0;
            bottom: 0;
            right: 0;
            width: 300px;
            background-image: radial-gradient(#C99E54 1.4px, transparent 1.4px);
            background-size: 22px 22px;
            opacity: 0.25;
            mask-image: linear-gradient(to left, rgba(0,0,0,0.8), transparent);
            -webkit-mask-image: linear-gradient(to left, rgba(0,0,0,0.8), transparent);
            pointer-events: none;
          }

          .policy-container {
            max-width: 860px;
            margin: 0 auto;
            background: linear-gradient(135deg, #fefaf4 0%, #fdf9f3 35%, #fefaf6 70%, #fdf9f6 100%);
            border-radius: 24px;
            border: 1px solid rgba(201, 158, 84, 0.25);
            box-shadow: inset 0 1px 2px rgba(255,255,255,.95), 0 0 0 1px rgba(255,233,194,.45), 0 10px 24px rgba(191,160,110,.10), 0 14px 35px rgba(180,150,100,.09), 0 50px 100px rgba(180,150,100,.06);
            overflow: hidden;
            position: relative;
            z-index: 1;
          }

          .policy-header {
            background: linear-gradient(180deg, #FFFFFF 0%, #FAF5EE 100%);
            color: #0F172A;
            padding: 3rem 2.5rem 2rem 2.5rem;
            text-align: center;
            border-bottom: 1px solid rgba(201, 158, 84, 0.2);
          }

          .policy-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 0.75rem;
            font-weight: 700;
            letter-spacing: 1.5px;
            color: #C99E54;
            background: rgba(201, 158, 84, 0.08);
            border: 1px solid rgba(201, 158, 84, 0.3);
            border-radius: 30px;
            padding: 6px 16px;
            margin-bottom: 1rem;
            text-transform: uppercase;
          }

          .star-icon {
            font-size: 0.85rem;
            color: #F0B040;
          }

          .policy-header h1 {
            font-size: 2.75rem;
            font-weight: 800;
            color: #0F172A;
            margin: 0 0 0.5rem 0;
            letter-spacing: -0.5px;
          }

          .gold-text {
            color: #C98E32;
            background: linear-gradient(135deg, #D99215 0%, #B88228 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .heading-divider {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin: 1rem 0 1.25rem 0;
          }

          .divider-line {
            height: 1px;
            width: 60px;
            background: linear-gradient(90deg, transparent, #C99E54, transparent);
          }

          .divider-diamond {
            width: 7px;
            height: 7px;
            background: #C99E54;
            transform: rotate(45deg);
          }

          .subnote {
            margin: 0 0 0.4rem 0;
            color: #64748B;
            font-size: 0.98rem;
            font-weight: 500;
          }

          .effective-date {
            font-size: 0.95rem;
            color: #94A3B8;
            margin: 0;
            font-weight: 500;
          }

          .policy-content {
            padding: 3rem 2.5rem;
            line-height: 1.8;
            color: #334155;
          }

          .intro {
            font-size: 1.08rem;
            margin-bottom: 1.5rem;
            padding: 1.5rem;
            background: rgba(201, 158, 84, 0.06);
            border-left: 4px solid #C99E54;
            border-radius: 0 12px 12px 0;
            color: #1E293B;
          }

          .policy-section {
            margin-bottom: 2.75rem;
          }

          .policy-section h2 {
            color: #0F172A;
            font-size: 1.45rem;
            font-weight: 700;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid rgba(201, 158, 84, 0.18);
            display: flex;
            align-items: center;
          }

          .policy-section p {
            margin-bottom: 1rem;
            font-size: 1.02rem;
            color: #334155;
          }

          .policy-section ul,
          .policy-section ol {
            margin: 1rem 0 1.25rem 0;
            padding-left: 1.6rem;
          }

          .policy-section li {
            margin-bottom: 0.6rem;
            font-size: 1.02rem;
            color: #334155;
          }

          .policy-section ol {
            list-style-type: decimal;
            list-style-position: outside;
          }

          .policy-section ol li::marker {
            font-weight: 700;
            color: #C99E54;
          }

          .contact-info {
            background: linear-gradient(135deg, #FFFDF9 0%, #FDF8F0 100%);
            padding: 1.75rem;
            border-radius: 14px;
            border: 1px solid rgba(201, 158, 84, 0.25);
            box-shadow: 0 4px 15px rgba(201, 158, 84, 0.06);
            margin-top: 1.25rem;
          }

          .contact-info p {
            margin: 0.5rem 0;
            font-size: 1.02rem;
          }

          .contact-info strong {
            color: #0F172A;
          }

          @media (max-width: 768px) {
            .policy-page-wrapper {
              padding: 6.5rem 1rem 2rem 1rem;
            }

            .policy-container {
              border-radius: 16px;
            }

            .policy-header {
              padding: 2.25rem 1.5rem 1.5rem 1.5rem;
            }

            .policy-header h1 {
              font-size: 2.1rem;
            }

            .policy-content {
              padding: 2rem 1.5rem;
            }

            .intro {
              font-size: 1rem;
              padding: 1.15rem;
            }

            .policy-section h2 {
              font-size: 1.3rem;
            }

            .policy-section p,
            .policy-section li {
              font-size: 0.98rem;
            }
          }

          @media (max-width: 480px) {
            .policy-page-wrapper {
              padding: 5.5rem 0.5rem 1.5rem 0.5rem;
            }

            .policy-header {
              padding: 1.75rem 1rem 1.25rem 1rem;
            }

            .policy-header h1 {
              font-size: 1.85rem;
            }

            .policy-content {
              padding: 1.5rem 1rem;
            }
          }
        `}</style>
      </div>
    </>
  );
}
